<script lang="ts">
  import Icon from './shared/Icon.svelte';
  import EmailReadImage from '../assets/icons/email-read.svg';
  import CheckmarkImage from '../assets/icons/checkmark.svg';
  import { t } from '../stores/translations';
  import Input from '../components/shared/Input.svelte';
  import Checkbox from '../components/shared/Checkbox.svelte';
  import Button from './shared/Button.svelte';
  import { advice } from '../stores/advice';
  import { locale } from '../stores/locale';
  import { googleTagManagerStore } from '../stores/googleTagManager';

  const reasonTranslationKeys = [
    'save.advice-information-reason-one',
    'save.advice-information-reason-two',
  ];

  const emailValidationRegex: RegExp =
    /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/;

  let nameInputValue: string;
  let emailInputValue: string;

  let nameError: string = null;
  let emailError: string = null;

  let emailSent: boolean = false;

  /**
   * Validates the form data and sets appropriate error messages if needed.
   */
  function validateFormData(): boolean {
    nameError = null;
    emailError = null;

    let formValid = true;

    if (!nameInputValue) {
      nameError = $t('save.advice-form-placeholders-required');
      formValid = false;
    }

    if (!emailInputValue) {
      emailError = $t('save.advice-form-placeholders-required');
      formValid = false;
    } else if (!emailValidationRegex.test(emailInputValue)) {
      emailError = $t('save.advice-form-placeholders-address-invalid');
      formValid = false;
    }

    return formValid;
  }

  /**
   * Sends an email to the given form data.
   *
   * @param event
   */
  async function sendEmail(event: SubmitEvent): Promise<void> {
    emailSent = false;
    if (!validateFormData()) return;

    const formData = new FormData(event.target as HTMLFormElement);
    const requestData = Object.fromEntries(formData.entries()) as Record<
      string,
      any
    >;

    requestData['newsletter'] = !!requestData['newsletter'] ? 1 : 0;
    requestData['language'] = $locale;
    Object.assign(requestData, $advice);

    try {
      await fetch(
        `https://${
          import.meta.env.VITE_CONFIGURATOR_API_URL
        }/api/email-questionnaire-pdf`,
        {
          method: 'POST',
          body: JSON.stringify(requestData),
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
        }
      );

      if ($googleTagManagerStore.gtmActive) {
        googleTagManagerStore.sendAdviceEmail();
      }
    } catch (_) {
    } finally {
      emailInputValue = null;
      nameInputValue = null;
      emailSent = true;
    }
  }
</script>

<div class="modal-content-container">
  <section class="info-section">
    <div class="email-read-icon-container">
      <Icon
        src={EmailReadImage}
        width="50px"
        height="43px"
        alt="Email read icon" />
    </div>

    <h2>{$t('save.advice-information-header')}</h2>

    <ul>
      {#each reasonTranslationKeys as reasonTranslationKey}
        <li>
          <Icon
            src={CheckmarkImage}
            width="1.5rem"
            height="1.5rem"
            alt="Checkmark icon" />

          <p>{$t(reasonTranslationKey)}</p>
        </li>
      {/each}
    </ul>
  </section>

  <section class="form-section">
    <h1>{$t('save.advice-form-header')}</h1>

    <form on:submit|preventDefault={sendEmail}>
      <div class="text-input">
        <Input
          bind:value={emailInputValue}
          name="receiver-email"
          id="receiver-email"
          error={emailError}
          placeholder={$t('save.advice-form-placeholders-address')} />
      </div>

      <div class="text-input">
        <Input
          bind:value={nameInputValue}
          name="receiver-name"
          id="receiver-name"
          placeholder={$t('save.advice-form-placeholders-name')}
          error={nameError} />
      </div>

      <div class="checkbox-input">
        <Checkbox
          name="newsletter"
          id="newsletter"
          label={$t('save.advice-form-checkbox')} />
      </div>

      <div class="submit-button">
        <Button>{$t('save.advice-button')}</Button>
      </div>

      {#if emailSent}
        <p class="email-sent-message">
          {$t('save.advice-form-send-notification')}
        </p>
      {/if}
    </form>
  </section>
</div>

<style lang="scss">
  @import 'src/lib/styles/variables.scss';
  @import 'src/lib/styles/mixins.scss';

  .modal-content-container {
    display: flex;
    padding: 1.5rem 1rem 1.5rem;
    flex-direction: column;

    & > .form-section {
      & > h1 {
        font-family: 'Roboto';
        font-weight: $font-weight-normal;
        font-size: $font-size-extra-large;
        color: $color-primary;
        margin: 2.5rem 0 0 0;
      }

      & > form {
        margin-top: 1.5rem;

        & > .text-input {
          margin-top: 1rem;
        }

        & > .checkbox-input {
          margin-top: 1rem;
        }

        & > .submit-button {
          margin-top: 1rem;
        }

        & > .email-sent-message {
          color: $color-primary;
          font-size: 0.875rem;
          margin: 0.5rem 0 0 0;
          max-width: 19.5rem;
          font-family: Roboto;
        }
      }
    }

    & > .info-section {
      padding-bottom: 2.5rem;
      border-bottom: 1px solid $color-advice-popup-divider;

      & > .email-read-icon-container {
        margin: auto;
        width: fit-content;
        padding-top: 0.5rem;
      }

      & > h2 {
        font-family: 'Roboto';
        font-weight: $font-weight-bold;
        font-size: 1.125rem;
        margin: 1rem 0 0 0;
        line-height: 1.2;
      }

      & > ul {
        list-style: none;
        padding: 0;
        margin: 1rem 0 0 0;

        & > li {
          display: flex;
          margin-bottom: 0.5rem;

          &:last-of-type {
            margin-bottom: 0;
          }

          & > p {
            margin: 0 0 0 1rem;
            font-family: 'Roboto';
            color: $color-text;
            font-size: $font-size-medium;
            line-height: normal;
          }
        }
      }
    }

    @include desktop {
      width: 736px;
      min-height: 269.58px;
      flex-direction: row-reverse;

        & > .info-section {
          order: 2;
          border-bottom: none;
          width: 308px;
          padding-bottom: 0;
          padding-right: 2.5rem;
          border-right: 1px solid $color-advice-popup-divider;
        }

        & > .form-section {
          order: 1;
          flex: 1;
          padding-left: 2.5rem;

          & > h1 {
            margin: 0;
          }

          & > form {
            & > .submit-button {
              margin-top: 1.625rem;
            }
          }
        }
      }
    }
</style>
